import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from './baseQuery';

/**
 * Cache tags. Every endpoint must declare what it provides or invalidates —
 * that is what removes the need for manual refetch calls after a mutation.
 * Add new tags here, never inline in a feature file.
 */
export const API_TAGS = [
  'Session',
  'Company',
  'AnnualReview',
  'Form',
  'Document',
  'Invoice',
  'Signature',
  'Subscription',
] as const;

/**
 * The single RTK Query API instance.
 *
 * Features do NOT call createApi themselves; they call `baseApi.injectEndpoints`
 * from their own `*.api.ts`. One instance means one cache, one middleware, and
 * cross-feature tag invalidation that actually works.
 *
 * The base query is the auth middleware (`./baseQuery`): it attaches the access
 * token and transparently refreshes it on a 401. Nothing else in the app should
 * read a token to build a request.
 */
export const baseApi = createApi({
  reducerPath: 'api',
  tagTypes: API_TAGS,
  baseQuery: baseQueryWithReauth,
  // Reconnect only. `refetchOnFocus` sounds right for a portal that gets
  // backgrounded, but `setupListeners` binds the document's `focus` event, not
  // just tab visibility — so every click that moves focus within the page
  // refetches EVERY mounted query. On Settings that meant general, subscription
  // and signatures re-firing as a group on each tap, and the Stripe Elements
  // iframes made it constant. Tag invalidation already refetches after each
  // mutation, so correctness never depended on this.
  refetchOnReconnect: true,
  keepUnusedDataFor: 60,
  endpoints: () => ({}),
});
